/**
 * PUE (personalized user experience) Phase 2: recent search chips.
 *
 * Capture: every `/search?q=…` page load (covers a submitted predictive
 * search, a plain URL, and clicking a chip itself — the term is added
 * again on arrival, which is also what "moves it to the front"), and every
 * predictive-search form submit directly (covers picking a product from
 * the dropdown without ever reaching /search). Both go through the same
 * `searches` store from Phase 0, whose own `normalize` already trims,
 * lower-cases for de-duplication, skips anything under 2 characters, and
 * — the one no caller here has to remember — refuses to store anything
 * shaped like an email or phone number.
 *
 * Render: every `[data-pag-recent-searches]` container on the page (the
 * search drawer's empty state and the search page both carry one). A
 * container that also carries `data-pag-recent-searches-context` only
 * shows when that attribute reads "eligible" — the search page sets it
 * server-side for "no query or no results"; the drawer has no such gate
 * and always shows chips when there are any.
 */

import { searches, CHANGE_EVENT } from '@theme/pag-personal-store';

const SELECTOR = '[data-pag-recent-searches]';

function searchUrl() {
  // `Theme` is a top-level `const` in snippets/scripts.liquid.
  /* global Theme */
  return (typeof Theme !== 'undefined' && Theme.routes?.search_url) || '/search';
}

/** @param {HTMLElement} container */
function render(container) {
  const track = container.querySelector('[data-pag-recent-searches-track]');
  if (!track) return;

  if (container.hasAttribute('data-pag-recent-searches-context')) {
    if (container.dataset.pagRecentSearchesContext !== 'eligible') {
      container.hidden = true;
      return;
    }
  }

  const terms = searches.all();

  if (terms.length === 0) {
    container.hidden = true;
    track.replaceChildren();
    return;
  }

  track.replaceChildren(
    ...terms.map((term) => {
      const chip = document.createElement('span');
      chip.className = 'pag-recent-search-chip';
      chip.setAttribute('role', 'listitem');

      const link = document.createElement('a');
      link.className = 'pag-recent-search-chip__term';
      link.href = `${searchUrl()}?q=${encodeURIComponent(term)}`;
      link.textContent = term;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'pag-recent-search-chip__remove';
      remove.setAttribute('aria-label', `Remove "${term}" from recent searches`);
      remove.dataset.pagRecentSearchRemove = term;
      remove.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';

      chip.append(link, remove);
      return chip;
    })
  );
  container.hidden = false;
}

function renderAll() {
  document.querySelectorAll(SELECTOR).forEach((container) => render(/** @type {HTMLElement} */ (container)));
}

function removeTerm(term) {
  searches.remove(term.toLocaleLowerCase());
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const removeButton = event.target.closest('[data-pag-recent-search-remove]');
  if (removeButton instanceof HTMLElement) {
    event.preventDefault();
    removeTerm(removeButton.dataset.pagRecentSearchRemove ?? '');
    return;
  }

  const clearButton = event.target.closest('[data-pag-recent-searches-clear]');
  if (clearButton) {
    event.preventDefault();
    searches.clear();
  }
});

// Delete/Backspace removes the chip currently focused (the term link or its
// own remove button), so a keyboard user never has to land exactly on the ✕.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Delete' && event.key !== 'Backspace') return;
  if (!(event.target instanceof Element)) return;
  const chip = event.target.closest('.pag-recent-search-chip');
  if (!chip) return;
  const term = chip.querySelector('[data-pag-recent-search-remove]')?.dataset.pagRecentSearchRemove;
  if (!term) return;
  event.preventDefault();
  removeTerm(term);
});

// Predictive search submit — the path that never reaches /search (the
// shopper clicked a product suggestion instead of the term itself).
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('predictive-search-form')) return;
  const input = form.querySelector('input[name="q"]');
  const term = input instanceof HTMLInputElement ? input.value : '';
  if (term) searches.add(term);
});

// The search results page itself — covers every other path, including a
// chip click, which is also what "moves it to the front" on return.
function captureFromResultsPage() {
  const marker = document.querySelector('[data-pag-search-term]');
  const term = marker instanceof HTMLElement ? marker.dataset.pagSearchTerm : '';
  if (term) searches.add(term);
}

document.addEventListener(CHANGE_EVENT, (event) => {
  if (event.detail?.list === 'searches') renderAll();
});

// The drawer's empty state is morphed back in after every reset, so its chip
// container is new markup each time.
document.addEventListener('shopify:section:load', renderAll);

captureFromResultsPage();
renderAll();
